import { sentryCreateBrowserRouter } from '@quadralize/sentry'
import { lazy, Suspense } from 'react'
import { AuthLayout } from '@/app/layouts/auth-layout'
import { ProtectedRoute } from '@/app/layouts/protected-route'
import { RootLayout } from '@/app/layouts/root-layout'
import { GenericErrorScreen, NotFoundScreen } from '@/modules/error'

const RouteLoader = () => (
  <div className="flex min-h-[60vh] items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
  </div>
)

const Home = lazy(() =>
  import('@/modules/discovery/screens/home').then((m) => ({ default: m.HomeScreen })),
)
const Search = lazy(() =>
  import('@/modules/discovery/screens/search').then((m) => ({ default: m.SearchScreen })),
)
const VenueDetail = lazy(() =>
  import('@/modules/venues/screens/detail').then((m) => ({ default: m.VenueDetailScreen })),
)
const BookingSuccess = lazy(() =>
  import('@/modules/booking/screens/success').then((m) => ({ default: m.BookingSuccessScreen })),
)
const Reservations = lazy(() =>
  import('@/modules/reservations/screens/list').then((m) => ({ default: m.ReservationsScreen })),
)
const ReservationDetail = lazy(() =>
  import('@/modules/reservations/screens/detail').then((m) => ({
    default: m.ReservationDetailScreen,
  })),
)
const Account = lazy(() =>
  import('@/modules/account/screens/account').then((m) => ({ default: m.AccountScreen })),
)

const AccountFavorites = lazy(() =>
  import('@/modules/account/screens/favorites').then((m) => ({ default: m.FavoritesScreen })),
)
const Login = lazy(() =>
  import('@/modules/auth/screens/login').then((m) => ({ default: m.LoginScreen })),
)
const Register = lazy(() =>
  import('@/modules/auth/screens/register').then((m) => ({ default: m.RegisterScreen })),
)
const ForgotPassword = lazy(() =>
  import('@/modules/auth/screens/forgot-password').then((m) => ({
    default: m.ForgotPasswordScreen,
  })),
)

const wrap = (node: React.ReactNode) => <Suspense fallback={<RouteLoader />}>{node}</Suspense>

export const router = sentryCreateBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <GenericErrorScreen />,
    children: [
      { path: '/', element: wrap(<Home />) },
      { path: '/buscar', element: wrap(<Search />) },
      { path: '/quadra/:venueId', element: wrap(<VenueDetail />) },
      { path: '/reserva/sucesso', element: wrap(<BookingSuccess />) },
      {
        path: '/reservas',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<RouteLoader />}>
              <Reservations />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: '/reservas/:reservationId',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<RouteLoader />}>
              <ReservationDetail />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: '/conta',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<RouteLoader />}>
              <Account />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: '/conta/favoritas',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<RouteLoader />}>
              <AccountFavorites />
            </Suspense>
          </ProtectedRoute>
        ),
      },
    ],
  },
  {
    element: <AuthLayout />,
    errorElement: <GenericErrorScreen />,
    children: [
      { path: '/entrar', element: wrap(<Login />) },
      { path: '/cadastro', element: wrap(<Register />) },
      { path: '/recuperar-senha', element: wrap(<ForgotPassword />) },
    ],
  },
  {
    path: '*',
    element: <NotFoundScreen />,
    errorElement: <GenericErrorScreen />,
  },
])
